import Big from 'big.js'
import uuidv4 from '@/utilities/uuid.js'
import presenters from '@/utilities/presenters.js'
import * as R from 'ramda'
import api from '@/api'
import { successPipeline } from '@/api/pipelines/success-pipeline.js'
import { totalCropExpense, totalCropIncome, roundTo } from '@/utilities/crop-calculations.js'

const state = {
  cropItems: {},
  cropCustomRateDetailItems: {},
  cropChemicalDetailItems: {},
  cropFertilizerDetailItems: {},
  cropSeedDetailItems: {},
  cropIrrigationDetailItems: {},
  cropOtherIncomeDetailItems: {},
  cropCommodityDetailItems: {},
  cropOtherExpenseDetailItems: {}
}

const mutations = {
  LOAD_CROP_ITEMS (state, payload) {
    state.cropItems = payload
  },

  LOAD_CROP_COMMODITY_DETAIL_ITEMS (state, payload) {
    state.cropCommodityDetailItems = payload
  },

  LOAD_CROP_OTHER_INCOME_DETAIL_ITEMS (state, payload) {
    state.cropOtherIncomeDetailItems = payload
  },

  LOAD_CROP_OTHER_EXPENSE_DETAIL_ITEMS (state, payload) {
    state.cropOtherExpenseDetailItems = payload
  },

  LOAD_CROP_CUSTOM_RATE_DETAIL_ITEMS (state, payload) {
    state.cropCustomRateDetailItems = payload
  },

  LOAD_CROP_SEED_DETAIL_ITEMS (state, payload) {
    state.cropSeedDetailItems = payload
  },

  LOAD_CROP_CHEMICAL_DETAIL_ITEMS (state, payload) {
    state.cropChemicalDetailItems = payload
  },

  LOAD_CROP_IRRIGATION_DETAIL_ITEMS (state, payload) {
    state.cropIrrigationDetailItems = payload
  },

  LOAD_CROP_FERTILIZER_DETAIL_ITEMS (state, payload) {
    state.cropFertilizerDetailItems = payload
  },

  ADD_CUSTOM_RATE_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.expenseCustomRateItems.push(itemId)
  },

  ADD_CROP_CUSTOM_RATE_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropCustomRateDetailItems,
      [id]: item
    }
    state.cropCustomRateDetailItems = items
  },

  UPDATE_CROP_CUSTOM_RATE_DETAIL_QUANTITY (state, { itemId, quantity }) {
    state.cropCustomRateDetailItems[itemId].quantity = quantity
  },

  ADD_CHEMICAL_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.expenseChemicalItems.push(itemId)
  },

  ADD_CROP_CHEMICAL_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropChemicalDetailItems,
      [id]: item
    }
    state.cropChemicalDetailItems = items
  },

  ADD_FERTILIZER_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.expenseFertilizerItems.push(itemId)
  },

  ADD_CROP_FERTILIZER_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropFertilizerDetailItems,
      [id]: item
    }
    state.cropFertilizerDetailItems = items
  },

  ADD_SEED_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.expenseSeedItems.push(itemId)
  },

  ADD_CROP_SEED_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropSeedDetailItems,
      [id]: item
    }
    state.cropSeedDetailItems = items
  },

  ADD_IRRIGATION_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.expenseIrrigationItems.push(itemId)
  },

  ADD_CROP_IRRIGATION_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropIrrigationDetailItems,
      [id]: item
    }
    state.cropIrrigationDetailItems = items
  },

  ADD_CROP_OTHER_EXPENSE_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropOtherExpenseDetailItems,
      [id]: item
    }
    state.cropOtherExpenseDetailItems = items
  },

  ADD_OTHER_EXPENSE_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.expenseOtherExpenseItems.push(itemId)
  },

  ADD_COMMODITY_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.incomeCommodityItems.push(itemId)
  },

  ADD_CROP_COMMODITY_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropCommodityDetailItems,
      [id]: item
    }
    state.cropCommodityDetailItems = items
  },

  ADD_CROP_OTHER_INCOME_DETAIL_ITEM (state, { id, item }) {
    const items = {
      ...state.cropOtherIncomeDetailItems,
      [id]: item
    }
    state.cropOtherIncomeDetailItems = items
  },

  ADD_OTHER_INCOME_ITEM_TO_CROP (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]
    crop.incomeOtherIncomeItems.push(itemId)
  },

  UPDATE_CROP_COMMODITY_DETAIL_PRODUCTION (state, { itemId, production }) {
    state.cropCommodityDetailItems[itemId].production = production
  },

  UPDATE_CROP_OTHER_INCOME_DETAIL_QUANTITY (state, { itemId, quantity }) {
    state.cropOtherIncomeDetailItems[itemId].quantity = quantity
  },

  UPDATE_CROP_OTHER_EXPENSE_DETAIL_QUANTITY (state, { itemId, quantity }) {
    state.cropOtherExpenseDetailItems[itemId].quantity = quantity
  },

  UPDATE_CROP_CHEMICAL_DETAIL_APPLICATION_RATE (state, { itemId, applicationRate }) {
    state.cropChemicalDetailItems[itemId].applicationRate = applicationRate
  },

  UPDATE_CROP_FERTILIZER_DETAIL_APPLICATION_RATE (state, { itemId, applicationRate }) {
    state.cropFertilizerDetailItems[itemId].applicationRate = applicationRate
  },

  UPDATE_CROP_SEED_DETAIL_SEEDING_RATE (state, { itemId, seedingRate }) {
    state.cropSeedDetailItems[itemId].seedingRate = seedingRate
  },

  UPDATE_CROP_IRRIGATION_DETAIL_QUANTITY (state, { itemId, quantity }) {
    state.cropIrrigationDetailItems[itemId].quantity = quantity
  },

  UPDATE_CROP_PRODUCTION (state, { itemId, production }) {
    state.cropItems[itemId].production = production
  },

  UPDATE_CROP_ITEM (state, { id, item }) {
    state.cropItems[id] = item
  },

  ADD_CROP_ITEM (state, { id, item }) {
    const items = {
      ...state.cropItems,
      [id]: item
    }
    state.cropItems = items
  },

  DELETE_CROP_ITEM (state, payload) {
    state.cropItems = R.omit(payload, state.cropItems)
  },

  DELETE_CROP_INCOME_COMMODITY_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.incomeCommodityItems = crop.incomeCommodityItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_COMMODITY_DETAIL_ITEM (state, payload) {
    state.cropCommodityDetailItems = R.omit(payload, state.cropCommodityDetailItems)
  },

  DELETE_CROP_INCOME_OTHER_INCOME_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.incomeOtherIncomeItems = crop.incomeOtherIncomeItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_OTHER_INCOME_DETAIL_ITEM (state, payload) {
    state.cropOtherIncomeDetailItems = R.omit(payload, state.cropOtherIncomeDetailItems)
  },

  DELETE_CROP_EXPENSE_OTHER_EXPENSE_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.expenseOtherExpenseItems = crop.expenseOtherExpenseItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_OTHER_EXPENSE_DETAIL_ITEM (state, payload) {
    state.cropOtherExpenseDetailItems = R.omit(payload, state.cropOtherExpenseDetailItems)
  },

  DELETE_CROP_EXPENSE_CUSTOM_RATE_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.expenseCustomRateItems = crop.expenseCustomRateItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_CUSTOM_RATE_DETAIL_ITEM (state, payload) {
    state.cropCustomRateDetailItems = R.omit(payload, state.cropCustomRateDetailItems)
  },

  DELETE_CROP_EXPENSE_CHEMICAL_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.expenseChemicalItems = crop.expenseChemicalItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_CHEMICAL_DETAIL_ITEM (state, payload) {
    state.cropChemicalDetailItems = R.omit(payload, state.cropChemicalDetailItems)
  },

  DELETE_CROP_EXPENSE_FERTILIZER_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.expenseFertilizerItems = crop.expenseFertilizerItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_FERTILIZER_DETAIL_ITEM (state, payload) {
    state.cropFertilizerDetailItems = R.omit(payload, state.cropFertilizerDetailItems)
  },

  DELETE_CROP_EXPENSE_SEED_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.expenseSeedItems = crop.expenseSeedItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_SEED_DETAIL_ITEM (state, payload) {
    state.cropSeedDetailItems = R.omit(payload, state.cropSeedDetailItems)
  },

  DELETE_CROP_EXPENSE_IRRIGATION_ITEM (state, { itemId, cropId }) {
    const crop = state.cropItems[cropId]

    crop.expenseIrrigationItems = crop.expenseIrrigationItems.filter((item) => item !== itemId)
  },

  DELETE_CROP_IRRIGATION_DETAIL_ITEM (state, payload) {
    state.cropIrrigationDetailItems = R.omit(payload, state.cropIrrigationDetailItems)
  }

}

const actions = {

  addCustomRateItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        quantity: jsConn.formParams.quantity,
        customRateId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropCustomRate(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Custom Rate successfully added to crop'
          // add the item to the cropExpenseCustomRateItems
          commit('ADD_CROP_CUSTOM_RATE_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_CUSTOM_RATE_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add custom rate to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Custom Rate successfully added to crop for this browser session'
      // add the item to the cropExpenseCustomRateItems
      commit('ADD_CROP_CUSTOM_RATE_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_CUSTOM_RATE_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  updateCropCustomRateDetailQuantity ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropCustomRate(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_CUSTOM_RATE_DETAIL_QUANTITY', jsConn.formParams)
          jsConn.toastMessage = 'Custom rate quantity successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update custom rate quantity'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_CUSTOM_RATE_DETAIL_QUANTITY', jsConn.formParams)
      jsConn.toastMessage = 'Custom rate quantity successfully updated for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  addChemicalItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    // get the expense item from the root store
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        applicationRate: jsConn.formParams.applicationRate,
        chemicalId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropChemical(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Chemical successfully added to crop'
          // add the item to the cropExpenseChemicalItems
          commit('ADD_CROP_CHEMICAL_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_CHEMICAL_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add chemical to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Chemical successfully added to crop for this browser session'
      // add the item to the cropExpenseChemicalItems
      commit('ADD_CROP_CHEMICAL_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_CHEMICAL_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  updateCropChemicalDetailApplicationRate ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropChemical(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_CHEMICAL_DETAIL_APPLICATION_RATE', jsConn.formParams)
          jsConn.toastMessage = 'Chemical application rate successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update chemical application rate'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_CHEMICAL_DETAIL_APPLICATION_RATE', jsConn.formParams)
      jsConn.toastMessage = 'Chemical application rate successfully updated'
      return Promise.resolve(jsConn)
    }
  },

  addFertilizerItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        applicationRate: jsConn.formParams.applicationRate,
        fertilizerId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropFertilizer(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Fertilizer successfully added to crop'
          // add the item to the cropExpenseFertilizerItems
          commit('ADD_CROP_FERTILIZER_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_FERTILIZER_ITEM_TO_CROP', params3)

          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add fertilizer to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Fertilizer successfully added to crop for this browser session'
      // add the item to the cropExpenseFertilizerItems
      commit('ADD_CROP_FERTILIZER_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_FERTILIZER_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  updateCropFertilizerDetailApplicationRate ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropFertilizer(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_FERTILIZER_DETAIL_APPLICATION_RATE', jsConn.formParams)
          jsConn.toastMessage = 'Fertilizer application rate successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update fertilizer application rate'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_FERTILIZER_DETAIL_APPLICATION_RATE', jsConn.formParams)
      jsConn.toastMessage = 'Fertilizer application rate successfully updated for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  addCommodityItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        production: jsConn.formParams.production,
        commodityId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropCommodity(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Commodity successfully added to crop'
          // add the item to the cropExpenseFertilizerItems
          commit('ADD_CROP_COMMODITY_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_COMMODITY_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add commodity to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Commodity successfully added to crop for this browser session'
      // add the item to the cropExpenseFertilizerItems
      commit('ADD_CROP_COMMODITY_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_COMMODITY_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  addOtherIncomeItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        quantity: jsConn.formParams.quantity,
        otherIncomeId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropOtherIncome(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Other income successfully added to crop'
          // add the item to the cropExpenseFertilizerItems
          commit('ADD_CROP_OTHER_INCOME_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_OTHER_INCOME_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add other income to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Other income successfully added to crop for this browser session'
      // add the item to the cropExpenseFertilizerItems
      commit('ADD_CROP_OTHER_INCOME_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_OTHER_INCOME_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  addOtherExpenseItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        quantity: jsConn.formParams.quantity,
        otherExpenseId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropOtherExpense(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Other expense successfully added to crop'
          // add the item to the cropExpenseOtherExpenseItems
          commit('ADD_CROP_OTHER_EXPENSE_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_OTHER_EXPENSE_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add other expense to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Other expense successfully added to crop for this browser session'
      // add the item to the cropExpenseOtherExpenseItems
      commit('ADD_CROP_OTHER_EXPENSE_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_OTHER_EXPENSE_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  addSeedItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        seedingRate: jsConn.formParams.seedingRate,
        seedId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropSeed(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Seed successfully added to crop'
          // add the item to the cropExpenseFertilizerItems
          commit('ADD_CROP_SEED_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_SEED_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add seed to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Seed successfully added to crop for this browser session'
      // add the item to the cropExpenseFertilizerItems
      commit('ADD_CROP_SEED_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_SEED_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  updateCropSeedDetailSeedingRate ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropSeed(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_SEED_DETAIL_SEEDING_RATE', jsConn.formParams)
          jsConn.toastMessage = 'Seeding rate successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update seeding rate'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_SEED_DETAIL_SEEDING_RATE', jsConn.formParams)
      jsConn.toastMessage = 'Seeding rate successfully updated'
      return Promise.resolve(jsConn)
    }
  },

  updateCropCommodityDetailProduction ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropCommodity(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_COMMODITY_DETAIL_PRODUCTION', jsConn.formParams)
          jsConn.toastMessage = 'Commodity production successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update commodity production'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Commodity production successfully updated for this browser session'
      commit('UPDATE_CROP_COMMODITY_DETAIL_PRODUCTION', jsConn.formParams)
      return Promise.resolve(jsConn)
    }
  },

  updateCropOtherIncomeDetailQuantity ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropOtherIncome(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_OTHER_INCOME_DETAIL_QUANTITY', jsConn.formParams)
          jsConn.toastMessage = 'Other income quantity successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update other income quantity'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_OTHER_INCOME_DETAIL_QUANTITY', jsConn.formParams)
      jsConn.toastMessage = 'Other income quantity successfully updated for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  updateCropOtherExpenseDetailQuantity ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropOtherExpense(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_OTHER_EXPENSE_DETAIL_QUANTITY', jsConn.formParams)
          jsConn.toastMessage = 'Other expense quantity successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update other expense quantity'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_OTHER_EXPENSE_DETAIL_QUANTITY', jsConn.formParams)
      jsConn.toastMessage = 'Other expense quantity successfully updated for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  updateCropIrrigationDetailQuantity ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.updateCropIrrigation(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('UPDATE_CROP_IRRIGATION_DETAIL_QUANTITY', jsConn.formParams)
          jsConn.toastMessage = 'Irrigation quantity successfully updated'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to update irrigation quantity'
          return Promise.reject(jsConn)
        })
    } else {
      commit('UPDATE_CROP_IRRIGATION_DETAIL_QUANTITY', jsConn.formParams)
      jsConn.toastMessage = 'Irrigation quantity successfully updated for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  addIrrigationItemToCrop ({ commit, getters, rootGetters }, jsConn) {
    const id = uuidv4()

    const params2 = {
      id: id,
      item: {
        cropId: jsConn.formParams.cropId,
        quantity: jsConn.formParams.quantity,
        irrigationId: jsConn.formParams.itemId
      }
    }

    const params3 = {
      itemId: id,
      cropId: jsConn.formParams.cropId
    }

    jsConn.formParams = params2

    if (getters.signedIn) {
      return api.createCropIrrigation(jsConn)
        .then(jsConn => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          jsConn.toastMessage = 'Irrigation successfully added to crop'
          // add the item to the cropExpenseFertilizerItems
          commit('ADD_CROP_IRRIGATION_DETAIL_ITEM', params2)
          // add the item to the crop
          commit('ADD_IRRIGATION_ITEM_TO_CROP', params3)
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to add irrigation to crop'
          return Promise.reject(jsConn)
        })
    } else {
      jsConn.toastMessage = 'Irrigation successfully added to crop for this browser session'
      // add the item to the cropExpenseFertilizerItems
      commit('ADD_CROP_IRRIGATION_DETAIL_ITEM', params2)
      // add the item to the crop
      commit('ADD_IRRIGATION_ITEM_TO_CROP', params3)
      return Promise.resolve(jsConn)
    }
  },

  updateCropItem ({ commit, getters }, jsConn) {
    if (jsConn.formParams.id === 'new') {
      const id = uuidv4()
      jsConn.formParams = {
        id: id,
        item: jsConn.formParams.item
      }

      if (getters.signedIn) {
        return api.createCrop(jsConn)
          .then(jsConn => {
            return successPipeline(jsConn)
          })
          .then(jsConn => {
            commit('ADD_CROP_ITEM', jsConn.formParams)
            jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully saved'
            return Promise.resolve(jsConn)
          })
          .catch(jsConn => {
            jsConn.toastMessage = 'Unable to save ' + jsConn.formParams['item']['description']
            return Promise.reject(jsConn)
          })
      } else {
        commit('ADD_CROP_ITEM', jsConn.formParams)
        jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully saved for this browser session'
        return Promise.resolve(jsConn)
      }
    } else {
      if (getters.signedIn) {
        return api.updateCrop(jsConn)
          .then(jsConn => {
            return successPipeline(jsConn)
          })
          .then(jsConn => {
            commit('UPDATE_CROP_ITEM', jsConn.formParams)
            jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully updated'
            return Promise.resolve(jsConn)
          })
          .catch(jsConn => {
            return Promise.reject(jsConn)
          })
      } else {
        commit('UPDATE_CROP_ITEM', jsConn.formParams)
        jsConn.toastMessage = jsConn.formParams['item']['description'] + ' successfully updated for this browser session'
        return Promise.resolve(jsConn)
      }
    }
  },

  deleteCropItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCrop(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          const crop = getters.getCropById(jsConn.formParams['id'])

          commit('DELETE_CROP_ITEM', [jsConn.formParams['id']])

          commit('DELETE_CROP_CUSTOM_RATE_DETAIL_ITEM', crop.expenseCustomRateItems)
          commit('DELETE_CROP_CHEMICAL_DETAIL_ITEM', crop.expenseChemicalItems)
          commit('DELETE_CROP_FERTILIZER_DETAIL_ITEM', crop.expenseFertilizerItems)
          commit('DELETE_CROP_SEED_DETAIL_ITEM', crop.expenseSeedItems)
          commit('DELETE_CROP_IRRIGATION_DETAIL_ITEM', crop.expenseIrrigationItems)
          commit('DELETE_CROP_OTHER_EXPENSE_DETAIL_ITEM', crop.expenseOtherExpenseItems)
          commit('DELETE_CROP_COMMODITY_DETAIL_ITEM', crop.incomeCommodityItems)
          commit('DELETE_CROP_OTHER_INCOME_DETAIL_ITEM', crop.incomeOtherIncomeItems)
          jsConn.toastMessage = 'Crop item deleted'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to delete crop item'
          return Promise.reject(jsConn)
        })
    } else {
      const crop = getters.getCropById(jsConn.formParams['id'])

      commit('DELETE_CROP_ITEM', [jsConn.formParams['id']])

      commit('DELETE_CROP_CUSTOM_RATE_DETAIL_ITEM', crop.expenseCustomRateItems)
      commit('DELETE_CROP_CHEMICAL_DETAIL_ITEM', crop.expenseChemicalItems)
      commit('DELETE_CROP_FERTILIZER_DETAIL_ITEM', crop.expenseFertilizerItems)
      commit('DELETE_CROP_SEED_DETAIL_ITEM', crop.expenseSeedItems)
      commit('DELETE_CROP_IRRIGATION_DETAIL_ITEM', crop.expenseIrrigationItems)
      commit('DELETE_CROP_OTHER_EXPENSE_DETAIL_ITEM', crop.expenseOtherExpenseItems)
      commit('DELETE_CROP_COMMODITY_DETAIL_ITEM', crop.incomeCommodityItems)
      commit('DELETE_CROP_OTHER_INCOME_DETAIL_ITEM', crop.incomeOtherIncomeItems)
      jsConn.toastMessage = 'Crop item deleted for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropIncomeCommodityItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropCommodity(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_INCOME_COMMODITY_ITEM', jsConn.formParams)
          commit('DELETE_CROP_COMMODITY_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Commodity item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove commodity item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_INCOME_COMMODITY_ITEM', jsConn.formParams)
      commit('DELETE_CROP_COMMODITY_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Commodity item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropIncomeOtherIncomeItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropOtherIncome(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_INCOME_OTHER_INCOME_ITEM', jsConn.formParams)
          commit('DELETE_CROP_OTHER_INCOME_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Other income item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove other income item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_INCOME_OTHER_INCOME_ITEM', jsConn.formParams)
      commit('DELETE_CROP_OTHER_INCOME_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Other income item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropExpenseCustomRateItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropCustomRate(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_EXPENSE_CUSTOM_RATE_ITEM', jsConn.formParams)
          commit('DELETE_CROP_CUSTOM_RATE_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Custom rate item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove custom rate item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_EXPENSE_CUSTOM_RATE_ITEM', jsConn.formParams)
      commit('DELETE_CROP_CUSTOM_RATE_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Custom rate item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropExpenseOtherExpenseItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropOtherExpense(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_EXPENSE_OTHER_EXPENSE_ITEM', jsConn.formParams)
          commit('DELETE_CROP_OTHER_EXPENSE_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Other expense item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove other expense item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_EXPENSE_OTHER_EXPENSE_ITEM', jsConn.formParams)
      commit('DELETE_CROP_OTHER_EXPENSE_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Other Expense item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCustomRateItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a custom rate is deleted
    const customRateId = jsConn.formParams['id']
    const customRateDetailItems = getters.cropCustomRateDetailItemsWhereCustomRateId(customRateId)

    // delete from the crops
    R.mapObjIndexed((customRateDetailItem, key, obj) => {
      const params = {
        cropId: customRateDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_EXPENSE_CUSTOM_RATE_ITEM', params)
      commit('DELETE_CROP_CUSTOM_RATE_DETAIL_ITEM', [params.itemId])
    }, customRateDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteChemicalItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a chemical is deleted
    const chemicalId = jsConn.formParams['id']
    const chemicalDetailItems = getters.cropChemicalDetailItemsWhereChemicalId(chemicalId)

    // delete from the crops
    R.mapObjIndexed((chemicalDetailItem, key, obj) => {
      const params = {
        cropId: chemicalDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_EXPENSE_CHEMICAL_ITEM', params)
      commit('DELETE_CROP_CHEMICAL_DETAIL_ITEM', [params.itemId])
    }, chemicalDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteFertilizerItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a fertilizer is deleted
    const fertilizerId = jsConn.formParams['id']
    const fertilizerDetailItems = getters.cropFertilizerDetailItemsWhereFertilizerId(fertilizerId)

    // delete from the crops
    R.mapObjIndexed((fertilizerDetailItem, key, obj) => {
      const params = {
        cropId: fertilizerDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_EXPENSE_FERTILIZER_ITEM', params)
      commit('DELETE_CROP_FERTILIZER_DETAIL_ITEM', [params.itemId])
    }, fertilizerDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteIrrigationItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a irrigation is deleted
    const irrigationId = jsConn.formParams['id']
    const irrigationDetailItems = getters.cropIrrigationDetailItemsWhereIrrigationId(irrigationId)

    // delete from the crops
    R.mapObjIndexed((irrigationDetailItem, key, obj) => {
      const params = {
        cropId: irrigationDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_EXPENSE_IRRIGATION_ITEM', params)
      commit('DELETE_CROP_IRRIGATION_DETAIL_ITEM', [params.itemId])
    }, irrigationDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteSeedItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a seed is deleted
    const seedId = jsConn.formParams['id']
    const seedDetailItems = getters.cropSeedDetailItemsWhereSeedId(seedId)

    // delete from the crops
    R.mapObjIndexed((seedDetailItem, key, obj) => {
      const params = {
        cropId: seedDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_EXPENSE_SEED_ITEM', params)
      commit('DELETE_CROP_SEED_DETAIL_ITEM', [params.itemId])
    }, seedDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteCommodityItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a commodity is deleted
    const commodityId = jsConn.formParams['id']
    const commodityDetailItems = getters.cropCommodityDetailItemsWhereCommodityId(commodityId)

    // delete from the crops
    R.mapObjIndexed((commodityDetailItem, key, obj) => {
      const params = {
        cropId: commodityDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_INCOME_COMMODITY_ITEM', params)
      commit('DELETE_CROP_COMMODITY_DETAIL_ITEM', [params.itemId])
    }, commodityDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteOtherIncomeItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a otherIncome is deleted
    const otherIncomeId = jsConn.formParams['id']
    const otherIncomeDetailItems = getters.cropOtherIncomeDetailItemsWhereOtherIncomeId(otherIncomeId)

    // delete from the crops
    R.mapObjIndexed((otherIncomeDetailItem, key, obj) => {
      const params = {
        cropId: otherIncomeDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_INCOME_OTHER_INCOME_ITEM', params)
      commit('DELETE_CROP_OTHER_INCOME_DETAIL_ITEM', [params.itemId])
    }, otherIncomeDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteOtherExpenseItemFromAllCrops ({ commit, getters }, jsConn) {
    // on the database these crop items are set to delete all
    // when a otherExpense is deleted
    const otherExpenseId = jsConn.formParams['id']
    const otherExpenseDetailItems = getters.cropOtherExpenseDetailItemsWhereOtherExpenseId(otherExpenseId)

    // delete from the crops
    R.mapObjIndexed((otherExpenseDetailItem, key, obj) => {
      const params = {
        cropId: otherExpenseDetailItem['cropId'],
        itemId: key
      }
      // commit to the store
      commit('DELETE_CROP_EXPENSE_OTHER_EXPENSE_ITEM', params)
      commit('DELETE_CROP_OTHER_EXPENSE_DETAIL_ITEM', [params.itemId])
    }, otherExpenseDetailItems)

    return Promise.resolve(jsConn)
  },

  deleteCropExpenseChemicalItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropChemical(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_EXPENSE_CHEMICAL_ITEM', jsConn.formParams)
          commit('DELETE_CROP_CHEMICAL_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Chemical item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove chemical item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_EXPENSE_CHEMICAL_ITEM', jsConn.formParams)
      commit('DELETE_CROP_CHEMICAL_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Chemical item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropExpenseFertilizerItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropFertilizer(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_EXPENSE_FERTILIZER_ITEM', jsConn.formParams)
          commit('DELETE_CROP_FERTILIZER_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Fertilizer item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove fertilizer item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_EXPENSE_FERTILIZER_ITEM', jsConn.formParams)
      commit('DELETE_CROP_FERTILIZER_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Fertilizer item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropExpenseSeedItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropSeed(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_EXPENSE_SEED_ITEM', jsConn.formParams)
          commit('DELETE_CROP_SEED_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Seed item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove commodity item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_EXPENSE_SEED_ITEM', jsConn.formParams)
      commit('DELETE_CROP_SEED_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Seed item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  },

  deleteCropExpenseIrrigationItem ({ commit, getters }, jsConn) {
    if (getters.signedIn) {
      return api.deleteCropIrrigation(jsConn)
        .then((jsConn) => {
          return successPipeline(jsConn)
        })
        .then(jsConn => {
          commit('DELETE_CROP_EXPENSE_IRRIGATION_ITEM', jsConn.formParams)
          commit('DELETE_CROP_IRRIGATION_DETAIL_ITEM', [jsConn.formParams.itemId])
          jsConn.toastMessage = 'Irrigation item removed from crop'
          return Promise.resolve(jsConn)
        })
        .catch(jsConn => {
          jsConn.toastMessage = 'Unable to remove irrigation item from crop'
          return Promise.reject(jsConn)
        })
    } else {
      commit('DELETE_CROP_EXPENSE_IRRIGATION_ITEM', jsConn.formParams)
      commit('DELETE_CROP_IRRIGATION_DETAIL_ITEM', [jsConn.formParams.itemId])
      jsConn.toastMessage = 'Irrigation item removed from crop for this browser session'
      return Promise.resolve(jsConn)
    }
  }
}

const getters = {
  cropItems: state => state.cropItems,

  cropItemsIdList: state => {
    return Object.keys(state.cropItems)
  },

  cropCustomRateDetailItemsWhereCustomRateId: (state) => (id) => {
    return R.filter((n) => {
      return n['customRateId'] === id
    }, state.cropCustomRateDetailItems)
  },

  cropIrrigationDetailItemsWhereIrrigationId: (state) => (id) => {
    return R.filter((n) => {
      return n['irrigationId'] === id
    }, state.cropIrrigationDetailItems)
  },

  cropChemicalDetailItemsWhereChemicalId: (state) => (id) => {
    return R.filter((n) => {
      return n['chemicalId'] === id
    }, state.cropChemicalDetailItems)
  },

  cropFertilizerDetailItemsWhereFertilizerId: (state) => (id) => {
    return R.filter((n) => {
      return n['fertilizerId'] === id
    }, state.cropFertilizerDetailItems)
  },

  cropSeedDetailItemsWhereSeedId: (state) => (id) => {
    return R.filter((n) => {
      return n['seedId'] === id
    }, state.cropSeedDetailItems)
  },

  cropOtherExpenseDetailItemsWhereOtherExpenseId: (state) => (id) => {
    return R.filter((n) => {
      return n['otherExpenseId'] === id
    }, state.cropOtherExpenseDetailItems)
  },

  cropCommodityDetailItemsWhereCommodityId: (state) => (id) => {
    return R.filter((n) => {
      return n['commodityId'] === id
    }, state.cropCommodityDetailItems)
  },

  cropOtherIncomeDetailItemsWhereOtherIncomeId: (state) => (id) => {
    return R.filter((n) => {
      return n['otherIncomeId'] === id
    }, state.cropOtherIncomeDetailItems)
  },

  cropItemsListWithProfit: (state, getters, rootState, rootGetters) => {
    return getters.cropItemsIdList.map((item) => {
      const profit = getters.getCropProfitTotalByCropId(item)
      const formattedProfit = roundTo(profit)
      const crop = getters.getCropById(item)
      return {
        id: item,
        description: crop.description,
        profit: profit,
        formattedProfit: formattedProfit,
        editMode: false,
        expenseCustomRateItems: crop.expenseCustomRateItems
      }
    })
  },

  getCropById: state => id => {
    return state.cropItems[id]
  },

  getCropCommodityDetailItemById: state => id => {
    return state.cropCommodityDetailItems[id]
  },

  getCropOtherIncomeDetailItemById: state => id => {
    return state.cropOtherIncomeDetailItems[id]
  },

  getCropOtherExpenseDetailItemById: state => id => {
    return state.cropOtherExpenseDetailItems[id]
  },

  getCropCustomRateDetailItemById: state => id => {
    return state.cropCustomRateDetailItems[id]
  },

  getCropChemicalDetailItemById: state => id => {
    return state.cropChemicalDetailItems[id]
  },

  getCropFertilizerDetailItemById: state => id => {
    return state.cropFertilizerDetailItems[id]
  },

  getCropSeedDetailItemById: state => id => {
    return state.cropSeedDetailItems[id]
  },

  getCropIrrigationDetailItemById: state => id => {
    return state.cropIrrigationDetailItems[id]
  },

  getCropIncomeCommodityTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropIncomeCommodityItemsByCropId(id)
    return totalCropIncome(items)
  },

  getCropIncomeOtherIncomeTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropIncomeOtherIncomeItemsByCropId(id)
    return totalCropIncome(items)
  },

  getCropExpenseOtherExpenseTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropExpenseOtherExpenseItemsByCropId(id)
    return totalCropExpense(items)
  },

  getCropExpenseCustomRateTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropExpenseCustomRateItemsByCropId(id)
    return totalCropExpense(items)
  },

  getCropExpenseChemicalTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropExpenseChemicalItemsByCropId(id)
    return totalCropExpense(items)
  },

  getCropExpenseFertilizerTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropExpenseFertilizerItemsByCropId(id)
    return totalCropExpense(items)
  },

  getCropExpenseSeedTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropExpenseSeedItemsByCropId(id)
    return totalCropExpense(items)
  },

  getCropExpenseIrrigationTotalByCropId: (state, getters) => (id) => {
    // get the presented details for the item
    const items = getters.getCropExpenseIrrigationItemsByCropId(id)
    return totalCropExpense(items)
  },

  getCropExpensesTotalByCropId: (state, getters) => (id) => {
    const customRateTotal = getters.getCropExpenseCustomRateTotalByCropId(id)
    const chemicalTotal = getters.getCropExpenseChemicalTotalByCropId(id)
    const fertilizerTotal = getters.getCropExpenseFertilizerTotalByCropId(id)
    const seedTotal = getters.getCropExpenseSeedTotalByCropId(id)
    const irrigationTotal = getters.getCropExpenseIrrigationTotalByCropId(id)
    const otherExpenseTotal = getters.getCropExpenseOtherExpenseTotalByCropId(id)

    // ***********************
    // total
    // ***********************
    return Number(
      customRateTotal
        .plus(chemicalTotal)
        .plus(fertilizerTotal)
        .plus(seedTotal)
        .plus(otherExpenseTotal)
        .plus(irrigationTotal))
  },

  getCropIncomeTotalByCropId: (state, getters) => (id) => {
    const commodityTotal = getters.getCropIncomeCommodityTotalByCropId(id)
    const otherIncomeTotal = getters.getCropIncomeOtherIncomeTotalByCropId(id)

    return Number(
      commodityTotal
        .plus(otherIncomeTotal)
    )
  },

  getCropProfitTotalByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const profit = new Big(getters.getCropIncomeTotalByCropId(id)).minus(getters.getCropExpensesTotalByCropId(id))

    return Number(profit)
  },

  getCropIncomeCommodityItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the income item details
      const incomeItem = getters.getCropCommodityDetailItemById(itemId)

      // get the item from the list of commodities
      const commodityItem = rootGetters.getCommodityItemById(incomeItem.commodityId)

      return presenters.cropCommodityItem(itemId, incomeItem, commodityItem)
    }, crop.incomeCommodityItems)
  },

  getCropIncomeOtherIncomeItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the income item details
      const incomeItem = getters.getCropOtherIncomeDetailItemById(itemId)

      // get the item from the list of commodities
      const otherIncomeItem = rootGetters.getOtherIncomeItemById(incomeItem.otherIncomeId)

      return presenters.cropOtherIncomeItem(itemId, incomeItem, otherIncomeItem)
    }, crop.incomeOtherIncomeItems)
  },

  getCropExpenseOtherExpenseItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the expense item details
      const expenseItem = getters.getCropOtherExpenseDetailItemById(itemId)

      // get the item from the list of other expenses
      const otherExpenseItem = rootGetters.getOtherExpenseItemById(expenseItem.otherExpenseId)

      return presenters.cropOtherExpenseItem(itemId, expenseItem, otherExpenseItem)
    }, crop.expenseOtherExpenseItems)
  },

  getCropExpenseCustomRateItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the expense item details
      const expenseItem = getters.getCropCustomRateDetailItemById(itemId)
      // get the item from the list of custom rates
      const customRateItem = rootGetters.getCustomRateItemById(expenseItem.customRateId)

      // present the items
      return presenters.cropCustomRateItem(itemId, expenseItem, customRateItem)
    }, crop.expenseCustomRateItems)
  },

  getCropExpenseChemicalItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)
    return R.map((itemId) => {
      // get the expense item details
      const expenseItem = getters.getCropChemicalDetailItemById(itemId)
      // get the item from the list of chemicals
      const chemicalItem = rootGetters.getChemicalItemById(expenseItem.chemicalId)
      // get the conversion for the item
      const convertUnit = rootGetters.getConvertUnitItemById(chemicalItem.convertUnitId)

      return presenters.cropChemicalItem(itemId, expenseItem, chemicalItem, convertUnit)
    }, crop.expenseChemicalItems)
  },

  getCropExpenseFertilizerItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the expense item details
      const expenseItem = getters.getCropFertilizerDetailItemById(itemId)

      // get the item from the list of fertilizers
      const fertilizerItem = rootGetters.getFertilizerItemById(expenseItem.fertilizerId)
      const convertUnit = rootGetters.getConvertUnitItemById(fertilizerItem.convertUnitId)

      return presenters.cropFertilizerItem(itemId, expenseItem, fertilizerItem, convertUnit)
    }, crop.expenseFertilizerItems)
  },

  getCropExpenseSeedItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the expense item details
      const expenseItem = getters.getCropSeedDetailItemById(itemId)

      // get the item from the list of seeds
      const seedItem = rootGetters.getSeedItemById(expenseItem.seedId)

      return presenters.cropSeedItem(itemId, expenseItem, seedItem)
    }, crop.expenseSeedItems)
  },

  getCropExpenseIrrigationItemsByCropId: (state, getters, rootState, rootGetters) => (id) => {
    const crop = getters.getCropById(id)

    return R.map((itemId) => {
      // get the expense item details
      const expenseItem = getters.getCropIrrigationDetailItemById(itemId)

      // get the item from the list of irrigation
      const irrigationItem = rootGetters.getIrrigationItemById(expenseItem.irrigationId)

      return presenters.cropIrrigationItem(itemId, expenseItem, irrigationItem)
    }, crop.expenseIrrigationItems)
  },

  getCropsForAutocomplete: (state, getters) => {
    return getters.cropItemsIdList.map((item) => {
      const crop = getters.getCropById(item)
      return {
        value: item,
        text: crop.description
      }
    })
  }

}

const cropModule = {
  state,
  mutations,
  actions,
  getters
}

export default cropModule
